import { useEffect, useMemo, useState } from "react";
import { Modal } from "./Modal";
import { loadPlanning, loadPlanningPhoto, savePlanningTeam, type PlanningDay, type PlanningPerson, type PlanningRow, type PlanningStatus } from "./planning";
import type { CrmFiche } from "./types";

const PLANNING_URL = "https://planning.app.aureliasuite.fr/";
const STATUS: Record<PlanningStatus, string> = {
  saisir: "À saisir",
  cours: "En cours",
  validee: "Validée",
  absent: "Absent",
};

export function PlanningLink({ fiche, onChange }: { fiche: CrmFiche; onChange: (fiche: CrmFiche) => void }) {
  const [week, setWeek] = useState(mondayStamp);
  const [people, setPeople] = useState<PlanningPerson[]>([]);
  const [rows, setRows] = useState<PlanningRow[]>([]);
  const [ready, setReady] = useState(true);
  const [message, setMessage] = useState("");
  const [picking, setPicking] = useState(false);
  const teamKey = (fiche.planning_team || []).join(",");

  useEffect(() => {
    let alive = true;
    const team = teamKey === "" ? [] : teamKey.split(",").map((id) => Number(id) || 0).filter((id) => id > 0);
    void loadPlanning(week, team)
      .then((view) => {
        if (!alive) return;
        setReady(view.ready);
        setMessage(view.message);
        setPeople(view.people);
        setRows(view.rows);
      })
      .catch((reason: unknown) => {
        if (!alive) return;
        setReady(false);
        setPeople([]);
        setRows([]);
        setMessage(reason instanceof Error ? reason.message : "Planning indisponible");
      });
    return () => { alive = false; };
  }, [week, fiche.id, teamKey]);

  const totals = useMemo(() => ({
    present: rows.filter((row) => row.status === "cours" || row.status === "validee").length,
    absent: rows.filter((row) => row.status === "absent").length,
    open: rows.filter((row) => row.status === "saisir" || row.status === "cours").length,
  }), [rows]);

  return (
    <section className="crm-card overflow-hidden rounded-2xl border border-slate-200 shadow-[0_10px_28px_rgb(15_23_42/7%)]">
      <h2 className="crm-head flex flex-wrap items-center gap-2 border-b px-4 py-2 text-sm font-semibold">
        <span>Planning</span>
        <span className="text-xs font-medium opacity-70">{weekTitle(week)}</span>
        <span className="ml-auto flex items-center gap-1">
          <button type="button" className="grid h-7 w-7 place-items-center rounded-full text-slate-500 hover:bg-black/5" aria-label="Semaine précédente" onClick={() => setWeek((current) => shiftWeek(current, -7))}>‹</button>
          <button type="button" className="grid h-7 w-7 place-items-center rounded-full text-slate-500 hover:bg-black/5" aria-label="Semaine suivante" onClick={() => setWeek((current) => shiftWeek(current, 7))}>›</button>
          <a className="ml-1 text-xs font-semibold underline" href={PLANNING_URL} target="_blank" rel="noopener noreferrer">Ouvrir dans Planning</a>
        </span>
      </h2>
      <div className="grid gap-3 p-4">
        {message ? <p className="text-sm text-slate-500">{message}</p> : null}
        {ready && fiche.planning_team.length > 0 ? (
          <p className="text-xs font-semibold text-slate-500">
            {totals.present} présent{totals.present > 1 ? "s" : ""}
            <span className="mx-1.5 opacity-40">·</span>
            {totals.absent} absent{totals.absent > 1 ? "s" : ""}
            <span className="mx-1.5 opacity-40">·</span>
            {totals.open} fiche{totals.open > 1 ? "s" : ""} ouverte{totals.open > 1 ? "s" : ""}
          </p>
        ) : null}
        {ready && fiche.planning_team.length === 0 ? <p className="text-sm text-slate-500">Choisis un ou plusieurs salariés. Le récap suit leur semaine, sans rien écrire dans Planning.</p> : null}
        {ready && fiche.planning_team.length > rows.length ? <p className="text-xs text-slate-500">Un salarié choisi n’est plus dans Planning.</p> : null}
        <ul className="grid gap-3">
          {rows.map((row) => (
            <li key={row.id} className="rounded-2xl border border-slate-200 p-3">
              <div className="flex flex-wrap items-start gap-2">
                <Face id={row.id} name={row.name} photo={row.photo} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold">{row.name}</span>
                  <span className="block truncate text-xs text-slate-500">{[row.role, row.store, row.motif].filter(Boolean).join(" · ")}</span>
                </span>
                <span className="text-xs text-slate-500">{hourLabel(row.hours, row.weekly_hours)}</span>
                <span className={statusClass(row.status)}>{STATUS[row.status]}</span>
              </div>
              <WeekBars days={row.days} />
              <div className="mt-3 grid grid-cols-3 gap-2">
                <Meter label="CP restants" value={`${trimHours(row.cp)} j`} />
                <Meter label={row.deficit > 0 && row.overtime <= 0 ? "Déficit" : "Heures sup"} value={extraLabel(row)} tone={row.deficit > 0 && row.overtime <= 0 ? "down" : row.overtime > 0 ? "up" : ""} />
                <Meter label="Cumul récup." value={`${row.cumul > 0 ? "+" : ""}${trimHours(row.cumul)} h`} tone={row.cumul < 0 ? "down" : ""} />
              </div>
            </li>
          ))}
        </ul>
        <div>
          <button type="button" className="rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold disabled:opacity-40" disabled={!ready || people.length === 0} onClick={() => setPicking(true)}>Choisir</button>
        </div>
      </div>
      {picking ? (
        <TeamPick
          people={people}
          selected={fiche.planning_team}
          onClose={() => setPicking(false)}
          onSave={async (ids) => {
            const saved = await savePlanningTeam(fiche.id, ids);
            onChange({ ...fiche, planning_team: saved });
            setPicking(false);
          }}
        />
      ) : null}
    </section>
  );
}

function TeamPick({ people, selected, onClose, onSave }: { people: PlanningPerson[]; selected: number[]; onClose: () => void; onSave: (ids: number[]) => Promise<void> }) {
  const [picked, setPicked] = useState<number[]>(selected);
  const [needle, setNeedle] = useState("");
  const [busy, setBusy] = useState(false);
  const [fault, setFault] = useState("");
  const shown = people.filter((person) => person.name.toLocaleLowerCase("fr").includes(needle.trim().toLocaleLowerCase("fr")));

  return (
    <Modal onClose={onClose} closeDisabled={busy} sheetClass="crm-pop w-full max-w-lg rounded-2xl border border-slate-200 p-5 shadow-[0_30px_80px_-36px_rgba(15,23,42,0.45)]">
      {(close) => (
        <div className="grid gap-3">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Salariés</p>
          <input className="rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Rechercher" value={needle} onChange={(event) => setNeedle(event.target.value)} />
          <ul className="max-h-72 space-y-1 overflow-auto">
            {shown.map((person) => {
              const on = picked.includes(person.id);
              return (
                <li key={person.id}>
                  <label className={on ? "crm-soft flex cursor-pointer items-center gap-3 rounded-xl px-2 py-1.5" : "flex cursor-pointer items-center gap-3 rounded-xl px-2 py-1.5 hover:bg-black/5"}>
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={on}
                      onChange={() => setPicked((current) => on ? current.filter((id) => id !== person.id) : [...current, person.id])}
                    />
                    <span className={on ? "crm-tick is-on" : "crm-tick"} aria-hidden>
                      {on ? (
                        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.8">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 12.5 10 17.5 19 7" />
                        </svg>
                      ) : null}
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium">{person.name}</span>
                      <span className="block truncate text-xs text-slate-500">{[person.role, person.store].filter(Boolean).join(" · ")}</span>
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
          {fault ? <p className="text-sm text-rose-700">{fault}</p> : null}
          <div className="flex justify-end gap-2">
            <button type="button" className="rounded-full px-3 py-1.5 text-xs font-semibold" onClick={close}>Annuler</button>
            <button
              type="button"
              className="crm-fill rounded-full px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
              disabled={busy}
              onClick={() => {
                setBusy(true);
                setFault("");
                void onSave(picked).catch((reason: unknown) => setFault(reason instanceof Error ? reason.message : "Équipe non enregistrée")).finally(() => setBusy(false));
              }}
            >{busy ? "Enregistrement…" : "Enregistrer"}</button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function WeekBars({ days }: { days: PlanningDay[] }) {
  if (days.length === 0) return null;
  return (
    <div className="mt-3 grid grid-cols-6 gap-1.5">
      {days.map((day, index) => {
        const scale = day.planned > 0 ? day.planned : 7;
        const height = day.kind === "vide" ? 8 : day.hours > 0 ? Math.max(18, Math.min(100, (day.hours / scale) * 100)) : 100;
        return (
          <div key={`${day.label}-${index}`} className="text-center">
            <div className="flex h-16 items-end rounded-lg bg-black/5 p-1">
              <div className={`w-full rounded-md ${barClass(day.kind)}`} style={{ height: `${height}%` }} />
            </div>
            <span className="mt-1 block text-[10px] font-semibold uppercase text-slate-500">{day.label}</span>
            <span className="block text-[10px] text-slate-500">{dayCaption(day)}</span>
          </div>
        );
      })}
    </div>
  );
}

function Face({ id, name, photo }: { id: number; name: string; photo: boolean }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    if (!photo) {
      setUrl("");
      return;
    }
    let alive = true;
    void loadPlanningPhoto(id).then((next) => {
      if (alive) setUrl(next);
    });
    return () => { alive = false; };
  }, [id, photo]);
  const letter = name.trim().charAt(0).toUpperCase() || "·";
  return (
    <span className="grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-full bg-black/5 text-xs font-semibold">
      {url ? <img src={url} alt="" className="h-full w-full object-cover" /> : letter}
    </span>
  );
}

function Meter({ label, value, tone = "" }: { label: string; value: string; tone?: "" | "up" | "down" }) {
  const toneClass = tone === "up" ? "crm-meter-up" : tone === "down" ? "crm-meter-down" : "text-slate-800";
  return (
    <div className="rounded-xl bg-black/5 px-2 py-2 text-center">
      <span className="block text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      <span className={`mt-0.5 block text-sm font-semibold ${toneClass}`}>{value}</span>
    </div>
  );
}

function barClass(kind: PlanningDay["kind"]): string {
  if (kind === "presence") return "crm-fill";
  if (kind === "cp") return "bg-orange-400";
  if (kind === "recup") return "bg-emerald-500";
  if (kind === "maladie") return "bg-rose-400";
  if (kind === "absence") return "bg-amber-400";
  if (kind === "repos") return "bg-slate-300";
  return "bg-slate-300/80";
}

function dayCaption(day: PlanningDay): string {
  if (day.kind === "cp") return "CP";
  if (day.kind === "recup") return "Récup";
  if (day.kind === "maladie") return "Maladie";
  if (day.kind === "repos") return "Repos";
  if (day.kind === "absence") return "Absence";
  if (day.hours > 0) return `${trimHours(day.hours)} h`;
  return "—";
}

function extraLabel(row: PlanningRow): string {
  if (row.deficit > 0 && row.overtime <= 0) return `−${trimHours(row.deficit)} h`;
  return `${row.overtime > 0 ? "+" : ""}${trimHours(row.overtime)} h`;
}

function statusClass(status: PlanningStatus): string {
  const tone = status === "validee" ? "bg-emerald-50 text-emerald-800" : status === "cours" ? "bg-amber-50 text-amber-800" : status === "absent" ? "bg-rose-50 text-rose-800" : "bg-slate-100 text-slate-600";
  return `rounded-full px-2 py-1 text-[11px] font-semibold ${tone}`;
}

function hourLabel(hours: number, weekly: number): string {
  const done = trimHours(hours);
  if (weekly > 0) return `${done} / ${trimHours(weekly)} h`;
  return hours > 0 ? `${done} h` : "";
}

function trimHours(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toLocaleString("fr-FR", { maximumFractionDigits: 2 });
}

function mondayStamp(date = new Date()): string {
  const copy = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  copy.setDate(copy.getDate() - ((copy.getDay() + 6) % 7));
  return stamp(copy);
}

function shiftWeek(week: string, days: number): string {
  const [year, month, day] = week.split("-").map(Number);
  const date = new Date(year || 2026, (month || 1) - 1, day || 1);
  date.setDate(date.getDate() + days);
  return stamp(date);
}

function stamp(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

function weekTitle(week: string): string {
  const [year, month, day] = week.split("-").map(Number);
  if (!year || !month || !day) return "";
  const start = new Date(year, month - 1, day);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  const left = start.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
  const right = end.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
  return `${left} – ${right}`;
}
